<script setup lang="ts">
import { toast } from "@/ui-plus/sonner";
import { getAddress } from "@lib/utils/checkGeocodeDistance";
import type { TaxSettings } from "@lib/zod/taxSchema";
import { Pencil, ChevronDown, ChevronRight, Info, Gift, Loader2, Route, MoonIcon } from "lucide-vue-next";
import SimpleScheduleWidget from "@/widgets-iframe/SimpleScheduleWidget.vue";
import PaymentMethods from "@/widgets-iframe/PaymentMethods.vue";
import OrderNote from "@/widgets-iframe/OrderNote.vue";
import { formatCurrency } from "@lib/utils/formatCurrency";
import { calculateCartBreakdown, feeBreakdownNameMap } from "@lib/utils/calculateCartBreakdown";
import { Input } from '@/components/ui/input';
import { Button } from "@/components/ui/button";
import { platformServiceFee } from "@lib/platformServiceFee";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogTrigger,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogScrollContent,
    DialogClose
} from "@/components/ui/dialog";
import { computed, onMounted, ref } from "vue";
import { listAvailablePaymentMethods } from '@lib/utils/listAvailablePaymentMethods';
import { STRIPE_PAYMENT_TYPE_ID, PAYPAL_PAYMENT_TYPE_ID } from "@lib/consts/paymentIds";
import { isOpenNow } from "@lib/utils/isOpenNow";
import CourierEmbedMap from '@/orders-manager/CourierEmbedMap.vue';
import { calculateDeliveryteamSubTotal } from "@lib/utils/calculateDeliveryteamSubTotal";
import { storeOrdersApiClient } from '@api-client/store_orders';
import { type courierRequestState_perOrderRef } from './state';
import type { ResType } from "@api-client/typeUtils";
import { placeStoreCourierOrder } from "./_placeStoreCourierOrder";

type CourierType = NonNullable<
    ResType<typeof storeOrdersApiClient.nearby_couriers[':storeId']['$post']>["data"]["couriers"]
>[number];

const props = defineProps<{
    courier: CourierType;
    state: typeof courierRequestState_perOrderRef.value[string];
    storeId: string;
    storeOrderId: string;
}>();

if (!props.state.deliveryteamOrderInfosType) {
    props.state.deliveryteamOrderInfosType = {};
}
props.state.deliveryteamOrderInfosType.lastUpdatedTS = Date.now();
props.state.deliveryteamOrderInfosType.deliveryteamId = props.courier.deliveryteamId;
props.state.deliveryteamOrderInfosType.deliveryteamName = props.courier.deliveryteamName;
props.state.deliveryteamOrderInfosType.deliveryteamAddress1 = props.courier.address1 ?? undefined;

// ========== Credit Promo START ==========
const fetchBalance = async () => {
    if (!props.state.cachedStoreData?.storeId) {
        console.error('storeId not found');
        toast.error('storeId not found');
        return null;
    }
    const { data, error } = await (await storeOrdersApiClient[':storeId'].get_deliveryteam_credit_as_store_buyer.$post({
        param: {
            storeId: props.state.cachedStoreData?.storeId,
        },
        json: {
            deliveryteamId: props.courier.deliveryteamId,
        },
    })).json();
    if (error) {
        console.error(error);
    } else if (data) {
        return data;
    }
    return null;
}
type BalanceType = ResType<typeof storeOrdersApiClient[':storeId']['get_deliveryteam_credit_as_store_buyer']['$post']>["data"];
const balance = ref<BalanceType | null>(null);
const useCreditPromoModel = ref(0);
const applyCreditPromo = () => {
    if (
        balance.value &&
        (props.courier?.creditPromoSettings) &&
        (props.courier?.creditPromoSettings).creditPromoSettings?.isCreditPromoEnabled &&
        useCreditPromoModel.value >= ((props.courier?.creditPromoSettings)?.creditPromoSettings?.minAmountToUse ?? 0)
    ) {
        const maxCreditToUse = Math.min(
            balance.value?.balance ?? 0,
            breakdown.value.buyerPays
        )
        if (useCreditPromoModel.value > maxCreditToUse) {
            useCreditPromoModel.value = maxCreditToUse;
        }
        props.state.deliveryteamOrderInfosType!.usedCredit = useCreditPromoModel.value;
        return;
    }
    toast.warning("Amount reset to 0");
    props.state.deliveryteamOrderInfosType!.usedCredit = 0;
}
onMounted(async () => {
    if (
        (props.courier?.creditPromoSettings)?.creditPromoSettings?.isCreditPromoEnabled
    ) {
        // this should be fetched without condition
        // buyer may have prior credit amount before disabling credit promo
        // no if seller says no credit promo then it is not allowed to use credit promo
        balance.value = await fetchBalance();
    }
});
// ========== Credit Promo END ==========

if (props.state.tipCourier) {
    if (!props.state.deliveryteamOrderInfosType.editableServiceFeeRates) {
        props.state.deliveryteamOrderInfosType.editableServiceFeeRates = {};
    }
    props.state.deliveryteamOrderInfosType.editableServiceFeeRates['tip-courier'] = {
        feePerOrder: props.state.tipCourier,
        ratePerOrder: undefined,
    }
    if (!props.courier.serviceFees?.customServiceFees) {
        props.courier.serviceFees!.customServiceFees = [];
    }
    // TODO: even deliveryteam has not custom tip fee settings, I must move/give configured TIP amount to deliveryteam
    if (!props.courier.serviceFees?.customServiceFees?.find(fee => fee.id === 'tip-courier')) {
        props.courier.serviceFees?.customServiceFees?.push({
            id: 'tip-courier',
            name: 'Tip',
            ratePerOrder: 10,
            feePerOrder: props.state.tipCourier,
            overrideServiceFeeTaxRate: undefined,
            isEditable: true,
            appliedOrderTypesArray: undefined, // deliveryteamOrders does not have order type, undefined: apply to all orders
        });
    }

}


const isPlaceOrderLoading = ref(false);
// ── Payment Methods ──
const selectedPaymentMethodId = ref<string | undefined>(undefined);

const availablePaymentMethods = computed(() => {
    if (!props.courier.paymentMethods) return [];
    return listAvailablePaymentMethods(props.courier.paymentMethods, 'ignored-order-type', props.courier.hasNegativeBalance);
});

const subtotal = computed(() => {
    return calculateDeliveryteamSubTotal(
        { lat: props.state.deliveryAddress?.lat ?? 0, lng: props.state.deliveryAddress?.lng ?? 0 },
        { lat: props.state.pickupAddress?.lat ?? 0, lng: props.state.pickupAddress?.lng ?? 0 },
        props.courier.deliveryZones?.zones ?? [],
        props.courier.taxSettings as TaxSettings,
        props.courier.serviceFees?.customServiceFees ?? [],
        props.state.deliveryteamOrderInfosType,
        props.courier.applyFeeTransform,
    )
});

const breakdown = computed(() => calculateCartBreakdown(
    subtotal.value,
    platformServiceFee,
    props.courier.supportPartnerServiceFee?.fee ?? null,
    props.courier.marketingPartnerServiceFee ?? null,
    props.courier.taxSettings as TaxSettings,
    props.courier.serviceFees?.calculationType ?? "INCLUDE",
    props.courier.serviceFees?.tolerableServiceFeeRate ?? 0,
    props.courier.serviceFees?.offerDiscountIfPossible ?? false,
    props.state.deliveryteamOrderInfosType?.usedCredit ?? 0, // usedCredit
    props.courier.creditPromoSettings?.creditPromoSettings ?? undefined,
    props.courier.firstOrdersPromoSettings?.firstOrdersPromoSettings ?? undefined,
    0 // deliveryteamUserOrderCount
));

const isCurrentlyOpen = computed(() => {
    return isOpenNow(props.courier.openHours?.timezone ?? null, props.courier.openHours?.openHours ?? null,
        props.state.deliveryteamOrderInfosType?.scheduled_at_ts ?? undefined);
});

// ── Place Order ──
const placeOrderButtonText = computed(() => {
    switch (selectedPaymentMethodId.value) {
        case STRIPE_PAYMENT_TYPE_ID:
            return 'Pay with Stripe';
        case PAYPAL_PAYMENT_TYPE_ID:
            return 'Pay with PayPal';
        default:
            return 'Place order';
    }
});

const placeOrder = async () => {
    await placeStoreCourierOrder(
        isPlaceOrderLoading,
        props.storeId,
        props.storeOrderId,
        props.courier,
        props.state,
        selectedPaymentMethodId.value,
    );
};
</script>

<template>
    <div class="flex-1 p-4 space-y-3">
        <!-- Payment Methods -->
        <PaymentMethods v-model="selectedPaymentMethodId"
                        :methods="availablePaymentMethods" />

        <SimpleScheduleWidget v-model="props.state.deliveryteamOrderInfosType!.scheduled_at_ts"
                              :weekly-hours="props.courier.openHours?.openHours ?? null"
                              :timezone="props.courier.openHours?.timezone ?? null"
                              notify-label="Courier will be notified at selected time"
                              closed-label="Closed" />

        <div v-if="!isCurrentlyOpen"
             class="p-2 rounded-md bg-destructive text-destructive-foreground font-bold flex items-center gap-2">
            <MoonIcon class="h-5 w-5" />
            Out of operation hours
        </div>

        <div class="w-full min-w-0 max-w-full p-4 rounded-lg border bg-card text-card-foreground shadow-sm my-4">
            <!-- Order Note -->
            <OrderNote v-if="props.state.deliveryteamOrderInfosType"
                       v-model="props.state.deliveryteamOrderInfosType.orderNote" />

            <!-- Out of delivery zone -->
            <div v-if="!subtotal.bestDeliveryZone"
                 class="p-2 bg-destructive text-destructive-foreground">
                Out of delivery area
            </div>

            <!-- Minimum Subtotal -->
            <!-- deliveryteam has no minimum subtotal -->
            <!-- <div v-if="offer.subtotal.bestDeliveryZone?.minCart && offer.subtotal.shownFee < offer.subtotal.bestDeliveryZone.minCart"
                             class="p-2 bg-destructive text-destructive-foreground">
                            Minimum Subtotal:
                            {{ formatCurrency(offer.subtotal.bestDeliveryZone.minCart, currency) }}
                        </div> -->

            <!-- Delivery Fee -->
            <div class="flex justify-between items-center"
                 v-if="subtotal.bestDeliveryZone">
                <span>Delivery Fee</span>
                <span class="text-right">
                    {{ formatCurrency(subtotal.bestDeliveryZone?.shownFee, props.courier.taxSettings?.currency) }}
                </span>
            </div>

            <!-- Custom Service Fees -->
            <div v-for="(customSFee, index) in subtotal.calculatedCustomServiceFees"
                 :key="index">
                <div class=""
                     v-if="customSFee.isEditable">
                    <details class="border border-foreground p-1 my-1 rounded-md">
                        <summary class="w-full cursor-pointer flex justify-between items-center">
                            <span class="">
                                {{ customSFee.name }}
                            </span>
                            <span class=" text-right">
                                {{ formatCurrency(customSFee.shownFee, props.courier.taxSettings?.currency) }}
                                <Pencil class="inline-block ml-2" />
                            </span>
                        </summary>
                        <div class="">
                            <div class="flex justify-end items-center gap-2 border-t border-foreground pt-2">
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.ratePerOrder = 0">0%</Button>
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.ratePerOrder = 10">10%</Button>
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.ratePerOrder = 20">20%</Button>
                            </div>
                            <div class="flex justify-end items-center my-2">
                                <span class="mr-2">
                                    Percentage:
                                </span>
                                <div class="relative w-44">
                                    <!-- currentCart?.editableServiceFeeRates?.[editableServiceFee.id] -->
                                    <Input type="number"
                                           :model-value="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.ratePerOrder"
                                           @update:model-value="(val) => {
                                            props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.ratePerOrder = Number(val);
                                        }"
                                           :min="0"
                                           :step="1"
                                           class="h-10 pr-14" />
                                    <span
                                          class="absolute right-3 top-1/2 -translate-y-1/2 text-xl font-bold text-muted-foreground pointer-events-none">%</span>
                                </div>
                            </div>
                            <div class="flex justify-end items-center gap-2 border-t border-foreground pt-2">
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.feePerOrder = 0">
                                    0
                                </Button>
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.feePerOrder = 10">
                                    10
                                </Button>
                                <Button variant="outline"
                                        size="sm"
                                        @click="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.feePerOrder = 20">
                                    20
                                </Button>
                            </div>
                            <div class="flex justify-end items-center my-2">
                                <span class="mr-2">
                                    Amount:
                                </span>
                                <div class="relative w-44">
                                    <!-- currentCart?.editableServiceFeeRates?.[editableServiceFee.id] -->
                                    <Input type="number"
                                           :model-value="props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.feePerOrder"
                                           @update:model-value="(val) => {
                                            props.state.deliveryteamOrderInfosType!.editableServiceFeeRates![customSFee.id]!.feePerOrder = Number(val);
                                        }"
                                           :min="0"
                                           :step="1"
                                           class="h-10 pr-14" />
                                    <span
                                          class="absolute right-3 top-1/2 -translate-y-1/2 text-xl font-bold text-muted-foreground pointer-events-none">{{ props.courier.taxSettings?.currency?.toUpperCase() ?? 'GBP' }}</span>
                                </div>
                            </div>
                        </div>
                    </details>
                </div>
                <div class="flex justify-between items-center"
                     v-else-if="customSFee.shownFee > 0">
                    <span class="">
                        {{ customSFee.name }}
                    </span>
                    <span class=" text-right">
                        {{ formatCurrency(customSFee.shownFee, props.courier.taxSettings?.currency) }}
                    </span>
                </div>
            </div>


            <!-- Tax and Fees -->
            <div class="flex justify-between items-center"
                 v-if="breakdown.buyerPaysTaxAndFeesShownFee > 0">
                <span>
                    {{ breakdown.buyerPaysTaxAndFeesName }}

                    <Popover>
                        <PopoverTrigger as-child>
                            <Info class="inline-block" />
                        </PopoverTrigger>
                        <PopoverContent align="start">
                            <div class="space-y-2">
                                <h3 class="font-semibold">What's included?</h3>
                                <div v-for="(fee, fIndex) in breakdown.buyerPaysTaxAndFees"
                                     :key="fIndex">
                                    <div class="flex justify-between items-center">
                                        <div class="font-medium">{{ feeBreakdownNameMap[fee.type].name }}
                                        </div>
                                        <div>{{ fee.currencyShownFee }}</div>
                                    </div>
                                    <div class="text-sm text-muted-foreground">
                                        {{ feeBreakdownNameMap[fee.type].note }}
                                    </div>
                                </div>
                            </div>
                            <div class="mt-4">
                                Fee details
                                <Popover>
                                    <PopoverTrigger as-child>
                                        <Info class="inline-block" />
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <div class="space-y-2">
                                            <div v-if="breakdown.allTransparencyBreakdown">
                                                <div v-for="(fee, fIndex2) in breakdown.allTransparencyBreakdown"
                                                     :key="fIndex2"
                                                     class="my-4">
                                                    <div class="flex justify-between items-center">
                                                        <div class="font-medium">
                                                            {{ feeBreakdownNameMap[fee.type].name }}
                                                        </div>
                                                        <div>
                                                            {{ fee.currencyShownFee }}
                                                        </div>
                                                    </div>
                                                    <div class="text-sm text-muted-foreground">
                                                        {{ feeBreakdownNameMap[fee.type].note }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </PopoverContent>
                    </Popover>
                </span>
                <span class="text-right">
                    {{ formatCurrency(breakdown.buyerPaysTaxAndFeesShownFee, props.courier.taxSettings?.currency) }}
                </span>
            </div>

            <!-- Discount -->
            <div class="flex justify-between items-center p-2 text-xl font-bold text-primary"
                 v-if="breakdown.discount > 0">
                <span>Discount</span>
                <span class="text-right">
                    -{{ formatCurrency(breakdown.discount,
                        props.courier.taxSettings?.currency) }}
                </span>
            </div>

            <!-- First Orders Promo -->
            <div v-if="
                props.courier.firstOrdersPromoSettings &&
                props.courier.firstOrdersPromoSettings.firstOrdersPromoSettings &&
                props.courier.firstOrdersPromoSettings.firstOrdersPromoSettings.isFirstOrdersPromoEnabled &&
                props.courier.firstOrdersPromoSettings.firstOrdersPromoSettings.discountAmount > 0 &&
                props.courier.firstOrdersPromoSettings.firstOrdersPromoSettings.orderCountNumber > 0"
                 class="mt-2">
                <div class="flex justify-between items-center p-2 text-xl font-bold text-primary"
                     v-if="breakdown.firstOrdersDiscount > 0">
                    <span>First Orders Promo</span>
                    <span>
                        -{{ formatCurrency(breakdown.firstOrdersDiscount,
                            props.courier.taxSettings?.currency) }}
                    </span>
                </div>
            </div>

            <!-- Used store credit -->
            <div class="flex justify-between items-center p-2 text-xl font-bold text-primary"
                 v-if="breakdown.usedCredit > 0">
                <span>Used Gift Credit</span>
                <span class="text-right">
                    -{{ formatCurrency(breakdown.usedCredit,
                        props.courier.taxSettings?.currency) }}
                </span>
            </div>

            <!-- Total -->
            <div class="flex justify-between items-center font-bold text-2xl py-4">
                <span>Total</span>
                <span class="text-right"
                      v-if="props.state.deliveryteamOrderInfosType?.usedCredit && props.state.deliveryteamOrderInfosType?.usedCredit > 0">
                    {{ formatCurrency(breakdown.buyerPaysAfterCreditPromo, props.courier.taxSettings?.currency) }}
                </span>
                <span v-else
                      class="text-right">
                    {{ formatCurrency(breakdown.buyerPays, props.courier.taxSettings?.currency) }}
                </span>
            </div>

            <!-- Earn credit -->
            <div class="border border-primary p-0 rounded-md text-center text-2xl cursor-pointer"
                 v-if="
                    props.courier.creditPromoSettings &&
                    props.courier.creditPromoSettings.creditPromoSettings &&
                    props.courier.creditPromoSettings.creditPromoSettings?.isCreditPromoEnabled &&
                    props.courier.creditPromoSettings?.creditPromoSettings?.earnRatePerOrder > 0 &&
                    breakdown.earnCreditAmountByOrdering > 0">
                <span class="text-xs text-muted-foreground">
                    Once this order is completed, you will earn
                </span>
                <br>
                <Gift class="min-w-6 min-h-6 inline-block text-primary" />
                {{ formatCurrency(breakdown.earnCreditAmountByOrdering, props.courier.taxSettings?.currency) }}
            </div>

            <Dialog
                    v-if="(props.courier?.creditPromoSettings)?.creditPromoSettings?.isCreditPromoEnabled && balance?.balance && balance?.balance > 0">
                <DialogTrigger as-child>
                    <Button variant="outline"
                            size="sm"
                            class="border border-primary cursor-pointer">
                        <Gift class="min-w-6 min-h-6 inline-block text-primary" />
                        Use your gift credit
                        {{ formatCurrency(balance?.balance ?? 0, props.courier.taxSettings?.currency) }}
                    </Button>
                </DialogTrigger>
                <DialogScrollContent>

                    <DialogHeader>
                        <DialogTitle>
                            <Gift class="min-w-8 min-h-8 inline-block mr-2" /> Your Credit Balance
                        </DialogTitle>
                        <DialogDescription class="text-xl text-foreground">
                            You have
                            {{ formatCurrency(balance?.balance ?? 0, props.courier.taxSettings?.currency) }}
                            gift credit
                        </DialogDescription>
                    </DialogHeader>

                    <div class="">
                        <div class="flex items-center gap-1.5 justify-center">
                            <Label for="balance">Amount to use:</Label>
                            <div class="relative flex-1">
                                <span
                                      class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                                    {{ props.courier.taxSettings?.currency?.toUpperCase() ?? 'GBP' }}</span>
                                <Input type="number"
                                       v-model="useCreditPromoModel"
                                       id="balance"
                                       :min="0"
                                       :step="0.1"
                                       class="h-10 pl-14"
                                       :max="Math.min((breakdown.buyerPays + 1), balance?.balance ?? 0)"
                                       @input="useCreditPromoModel = Number($event.target.value) || 0" />
                            </div>
                            <DialogClose as-child>
                                <Button variant="default"
                                        class="h-10"
                                        @click="applyCreditPromo">
                                    Apply
                                </Button>
                            </DialogClose>
                        </div>

                        <div v-if="useCreditPromoModel < ((props.courier?.creditPromoSettings)?.creditPromoSettings?.minAmountToUse ?? 0)"
                             class="bg-destructive text-destructive-foreground p-2 my-4">
                            min amount to use:
                            {{ formatCurrency(
                                (props.courier?.creditPromoSettings)?.creditPromoSettings?.minAmountToUse ?? 0,
                                props.courier.taxSettings?.currency)
                            }}
                        </div>
                        <div v-if="useCreditPromoModel > (balance?.balance ?? 0)"
                             class="bg-destructive text-destructive-foreground p-2 my-4">
                            max amount to use:
                            {{ formatCurrency(balance?.balance ?? 0, props.courier.taxSettings?.currency) }}
                        </div>
                        <div v-if="useCreditPromoModel > (breakdown.buyerPays ?? 0)"
                             class="bg-destructive text-destructive-foreground p-2 my-4">
                            max amount to use:
                            {{ formatCurrency(breakdown.buyerPays ?? 0, props.courier.taxSettings?.currency) }}
                        </div>
                        <!-- TODO: do we have rules for delivery team? i do not remember it -->
                        <!-- <Rules /> -->
                    </div>

                </DialogScrollContent>
            </Dialog>


        </div>

        <!-- Place Order Button -->
        <Button size="lg"
                @click="placeOrder"
                id="place-order-button"
                :disabled="isPlaceOrderLoading"
                class="w-full text-lg font-bold hover:bg-primary/80 cursor-pointer mt-4">
            <Loader2 class="animate-spin inline-block mr-2"
                     v-if="isPlaceOrderLoading" />
            {{ placeOrderButtonText }}
            <ChevronRight class="inline-block ml-2 min-w-10 min-h-10" />
        </Button>


        <!-- Courier distance from pickup -->

        <Collapsible class="my-2 border rounded-md overflow-hidden">
            <CollapsibleTrigger class="w-full px-4 py-3  bg-background hover:bg-muted/50 cursor-pointer">
                <div class="flex items-center gap-2 justify-between">
                    <Route class="min-w-6 min-h-6 inline-block" />
                    <span v-if="props.courier.distance">
                        <strong class="text-foreground">~{{ props.courier.distance }}
                            {{ props.courier.measure }}</strong> away from the pickup
                        location.
                    </span>
                    <ChevronDown class="flex-shrink-0 transition-transform duration-200" />
                </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <!-- Courier Map -->
                <!-- :destination="`${deliveryAddress.lat},${deliveryAddress.lng}`"
                            :waypoints="[`${pickupAddress.lat},${pickupAddress.lng}`]" -->
                <CourierEmbedMap v-if="courier.currentLat && courier.currentLng && props.state.pickupAddress?.lat && props.state.pickupAddress?.lng && props.state.deliveryAddress?.lat && props.state.deliveryAddress?.lng"
                                 :is-link="true"
                                 :origin="`${courier.currentLat},${courier.currentLng}`"
                                 :destination="getAddress(props.state.deliveryAddress, true)"
                                 :waypoints="[getAddress(props.state.pickupAddress, true)]"
                                 :travel-mode="courier.mode" />
            </CollapsibleContent>
        </Collapsible>
    </div>
</template>